import { useMemo } from 'react';
import { useDropdown } from '../../hooks/useDropdown';
import clsx from 'clsx';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { setCellBackground } from '@platejs/table';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import textColorStyles from '../TextColorMenu/TextColorMenu.module.scss';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';
import { useRecentColors } from '../../hooks/useRecentColors';
import { ColorPickerPanel } from '../ColorPickerPanel/ColorPickerPanel';

// Light-tint palette for cell backgrounds — saturated text colors would make
// cell contents unreadable, so the quick swatches stay soft. The shared
// ColorPickerPanel's custom dialog still gives full-spectrum access when needed.
const PALETTE = ['#ffffff', '#f1f1ef', '#f4eee4', '#fbecdd', '#fbf3db', '#edf3ec', '#e7f3f8', '#f4f0f7', '#f9ecf2', '#fdebec'];

// Caret-in-cell lookup. Used to read the CURRENT cell's bg for the swatch
// preview when there's no multi-cell selection.
function findCellEntry(editor: any) {
    try {
        const at = editor?.selection;
        if (!at) return null;
        const entry = editor?.api?.above?.({ at, match: (n: any) => n?.type === 'td' || n?.type === 'th' });
        return entry ?? null;
    } catch {
        return null;
    }
}

// Multi-cell selection detector. Uses Plate's public `api.table.isSelectingCell()`
// instead of poking at the private `_selectedCellIds` option — the latter wasn't
// re-triggering useEditorSelector, which kept the button disabled on multi-cell
// drag-select.
function isSelectingCell(editor: any): boolean {
    try {
        return Boolean(editor?.api?.table?.isSelectingCell?.() ?? editor?.getApi?.({ key: 'table' })?.table?.isSelectingCell?.());
    } catch {
        return false;
    }
}

// Multi-cell: Plate's setCellBackground paints every selected cell at
// once (pass selectedCells explicitly — leaving it implicit no-ops in
// v53 when there's no active drag-selection).
// Single-cell: setCellBackground silently no-ops without selectedCells,
// so we fall back to a direct setNodes on the caret's cell path. Write
// the same key (`background`) as Plate's setCellBackground so both paths
// land in the same prop and the TableCellElement renderer (which reads
// background ?? backgroundColor) picks it up. Also clear the legacy
// `backgroundColor` so a re-painted cell doesn't keep its stale tint.
//
// CLEAR semantics: writing `background: 'transparent'` (rather than
// deleting the prop) is load-bearing for HEADER cells (`<th>`) — the
// base CSS sets `th { background: #f7f6f3 }`, so removing the inline
// style would let that default come back through.
export function paintCellBackground(editor: any, color: string | null): void {
    try {
        const ed: any = editor;
        const tableApi = ed?.api?.table ?? ed?.getApi?.({ key: 'table' })?.table;
        const selected = tableApi?.getSelectedCells?.();
        const bgValue = color ?? 'transparent';
        if (Array.isArray(selected) && selected.length > 1) {
            (setCellBackground as any)(ed, { color: bgValue, selectedCells: selected });
            // Wipe the legacy `backgroundColor` field on every painted cell
            // for parity with the single-cell path. getSelectedCells returns
            // cell NODES (not [node, path] entries), so match by node
            // identity rather than indexing a non-existent path.
            try {
                ed?.tf?.setNodes?.({ backgroundColor: null }, { match: (n: any) => selected.includes(n) });
            } catch {
                /* defensive */
            }
        } else {
            const entry = findCellEntry(ed);
            if (entry) {
                const [, path] = entry;
                ed?.tf?.setNodes?.({ background: bgValue, backgroundColor: null }, { at: path });
            }
        }
    } catch {
        /* defensive */
    }
}

export function CellBackgroundMenu() {
    const editor = useEditorRef();
    const { open, setOpen, ref: wrapRef } = useDropdown();
    const [recents, pushRecent] = useRecentColors('plateEditor.recentColors.cellBg');

    const visibleRecents = useMemo(() => {
        const paletteSet = new Set(PALETTE.map((c) => c.toLowerCase()));
        return recents.filter((c) => !paletteSet.has(c.toLowerCase()));
    }, [recents]);

    // Enabled when EITHER the caret is inside a single cell OR a multi-cell
    // drag-selection is active. Single-cell case also reads the current
    // background to drive the swatch preview.
    const state: { inCell: boolean; cellBg: string } = useEditorSelector((ed: any) => {
        if (isSelectingCell(ed)) {
            return { inCell: true, cellBg: '' };
        }
        const entry = findCellEntry(ed);
        if (entry) {
            const node: any = entry[0];
            // Plate's setCellBackground writes `background`. We also fall
            // back to the legacy `backgroundColor` for cells set via our
            // earlier code (or loaded from the Slate-shape stored format).
            return { inCell: true, cellBg: node?.background ?? node?.backgroundColor ?? '' };
        }
        return { inCell: false, cellBg: '' };
    }, []);

    const { inCell, cellBg } = state;

    const applyColor = (hex: string) => {
        paintCellBackground(editor, hex);
        setOpen(false);
    };
    const clearColor = () => {
        paintCellBackground(editor, null);
        setOpen(false);
    };

    const swatchColor = cellBg && cellBg !== '' ? cellBg : undefined;
    const value = swatchColor && /^#/.test(swatchColor) ? swatchColor : undefined;

    return (
        <div className={textColorStyles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(toolbarStyles.btn, open && toolbarStyles.btnActive, !inCell && toolbarStyles.btnDisabled)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    if (!inCell) return;
                    setOpen((o) => !o);
                }}
                disabled={!inCell}
                title={inCell ? 'Cell background' : 'Place caret inside a table cell to color it'}
                aria-label="Cell background color"
                aria-haspopup="true"
                aria-expanded={open}
                style={swatchColor ? { color: swatchColor } : undefined}
            >
                <FormatColorFillIcon fontSize="small" />
            </button>
            {open && inCell && (
                <div
                    className={textColorStyles.popover}
                    role="menu"
                    aria-label="Pick cell background"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <ColorPickerPanel
                        value={value}
                        previewColor={value}
                        palette={PALETTE}
                        recents={visibleRecents}
                        onPick={applyColor}
                        pushRecent={pushRecent}
                        onClear={clearColor}
                        clearLabel="Clear cell color"
                    />
                </div>
            )}
        </div>
    );
}
